import React, { useState, useEffect, useContext } from 'react';
import Header from './payment_advance/Header';
import ChargesGrid from './payment_advance/ChargeGrid';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '@mui/material/Button';
import logo from '../wisdom.png';
import FileUploadComponent from '../utils/FileUploadComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faFileAlt, faExternalLinkAlt, faArrowDown, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { DataContext } from '../contexts/DataContext';
import { useAuth, AuthProvider } from '../contexts/AuthContext';
import { generateAndUploadPDF } from '../utils/PaymentUtils';

const initialChargesData = {
  tax_expenses: [],
  actual_expenses: [],
  custom_brokerage_expenses: []
};

const PaymentAdvance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const passedRowData = location.state?.rowData || {};
  const paymentAdvanceId = location.state?.paymentAdvanceId;
  const { isViewOnly } = location.state || {};
  const { triggerRefetch } = useContext(DataContext);
  const { currentUser } = useAuth();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filesList, setFilesList] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'uploaded', direction: 'ascending' });
  const [alternateCustomer, setAlternateCustomer] = useState('');


  const handleSort = (key) => {
    sortFiles(key);
  };

  const fetchPresignedUrl = async (fileId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/${fileId}`);
      if (!response.ok) throw new Error('Failed to fetch pre-signed URL');
      const data = await response.json();
      return data.fileUrl; // Assuming the backend returns { fileUrl: '...' }
    } catch (error) {
      console.error('Error fetching pre-signed URL:', error);
      throw error;
    }
  };

  // Replace direct S3 URL access with a function to fetch and navigate to the pre-signed URL
  const handleViewFile = async (fileId) => {
    try {
      const url = await fetchPresignedUrl(fileId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening file:', error);
    }
  };

  useEffect(() => {
    const fetchFilesForEntity = async () => {
      try {
        const entityType = "PaymentAdvance";
        if (paymentAdvanceId && entityType) {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/entity/${paymentAdvanceId}?entityType=${entityType}`);
          setFilesList(response.data.files);
        }
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    };
    fetchFilesForEntity();
  }, [paymentAdvanceId]);

  useEffect(() => {
    const fetchAdvanceDetails = async () => {
      const trafficReference = location.state?.rowData?.traffic_reference;
      if (trafficReference) {
        try {
          const url = `${process.env.REACT_APP_BACKEND_API_URL}/api/references/getReferenciaCuentaAnticipos?traffic_reference=${encodeURIComponent(trafficReference)}`;
          const response = await axios.get(url);
          // Assuming the response.data now contains the `Saldo` directly
          setHeaderData(prev => ({ ...prev, advanceBalance: response.data.Saldo || 'No advance balance available' }));
        } catch (error) {
          console.error('Error fetching payment advance details:', error);
        }
      }
    };
    fetchAdvanceDetails();
  }, [location.state?.rowData]);

  const getPresignedUrlAndUpload = async (file) => {
    console.log('File to upload:', file.name, file.type);
    try {
      if (!file.name || !file.type) {
        throw new Error('File name or type is undefined.');
      }

      // Generate a unique identifier
      const uniqueId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const uniqueFileName = `${uniqueId}_${file.name}`;

      // Request a presigned URL from your backend with the unique file name
      const queryParams = new URLSearchParams({
        filename: uniqueFileName, // Use the unique file name here
        filetype: file.type,
      });
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/get-presigned-url?${queryParams.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to get presigned URL from backend');
      }
      const { url } = await response.json();

      // Upload the file to S3 using the presigned URL
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Return the file metadata including the unique file name
      const fileUrl = url.split('?')[0];
      return {
        filename: uniqueFileName,
        s3Key: uniqueFileName,
        s3Url: fileUrl,
      };
    } catch (error) {
      console.error('Error getting presigned URL or uploading file:', error);
      throw error;
    }
  };


  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const onFileUploaded = (fileInfo) => {
    setUploadedFiles((currentFiles) => [...currentFiles, fileInfo]);
  };

  const onFilesSelected = (filesWithClassification) => {
    setSelectedFiles(filesWithClassification); // Assuming this is the state update method in your parent component
  };

  const handleSavePaymentAdvance = async () => {
    //console.log("Attempting to save with selected files:", selectedFiles);

    if (!validateData()) {
      alert("Please fill out all mandatory fields before saving.");
      return;
    }

    const combinedFiles = [...filesList, ...selectedFiles.map(file => ({ ...file.file, category: file.classification }))];

    // Ensure there are no unclassified files when creating a new PaymentAdvance or editing an existing one
    const hasUnclassifiedFile = selectedFiles.some(file => file.classification === 'UNCLASSIFIED');
    if (hasUnclassifiedFile) {
      alert("All files must have a valid classification before saving.");
      return;
    }

    // Ensure there's at least one file for new PaymentAdvance or files are already uploaded for an existing PaymentAdvance
    if (!paymentAdvanceId && selectedFiles.length === 0) {
      alert("Please upload at least one file before saving.");
      return;
    }

    if (paymentAdvanceId && filesList.length === 0 && selectedFiles.length === 0) {
      alert("At least one file must be associated with the Payment Advance record.");
      return;
    }

    const hasComprobanteDeAnticipo = combinedFiles.some(file => file.category === 'COMPROBANTE DE ANTICIPO');
    const hasFacturaPDF = combinedFiles.some(file => file.category === 'FACTURA PDF');
    const hasFacturaXML = combinedFiles.some(file => file.category === 'FACTURA XML');
    const shouldCloseAdvance = hasFacturaPDF && hasFacturaXML && hasComprobanteDeAnticipo;

    let status;
    if (paymentAdvanceId) {
      if (shouldCloseAdvance) {
        status = 'closed';
      } else {
        status = hasComprobanteDeAnticipo ? 'advance_received' : 'open';
      }
    } else {
      if (shouldCloseAdvance) {
        status = 'closed';
      } else {
        status = hasComprobanteDeAnticipo ? 'advance_received' : 'open';
      }
    }


    let currentPaymentAdvanceId = paymentAdvanceId;

    try {
      const paymentAdvanceData = {

        traffic_reference: headerData.reference,
        entry_point: headerData.entry_point,
        license: headerData.license,
        pedimento: headerData.pedimento,
        advance_balance: headerData.advanceBalance,
        tipo_pedimento: headerData.tipo_pedimento,
        status_pedimento: headerData.status_pedimento,
        status_modulacion: headerData.status_modulacion,
        number_of_reference_invoices: headerData.number_of_reference_invoices,
        container_data: headerData.container_data,
        bill_of_lading_data: headerData.bill_of_lading_data,
        alternate_customer: alternateCustomer,
        customer: headerData.customer,
        customer_data: {
          customer_key: headerData.customer_key,
          customer_name: headerData.customer,
          customer_rfc: headerData.customer_rfc,
        },
 
        total_requested_payment_advance: 0,
        actual_payment_advance: 0,
        total_taxes: 0,
        total_actual_expenses: 0,
        total_custom_brokerage: 0,
        tax_expenses: expenses.tax_expenses,
        actual_expenses: expenses.actual_expenses,
        custom_brokerage_expenses: expenses.custom_brokerage_expenses,
        status: status
      };

      let differencesExist = false;
      let differencesList = { hasDifferences: false, differences: [] };
      if (paymentAdvanceId) {
        try {
          const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/compare/${paymentAdvanceId}`, paymentAdvanceData);
          differencesList = data;
          if (data.hasDifferences) {
            differencesExist = true;
          } else {
            differencesExist = false;
          }
        } catch (error) {
          console.error('Error during comparison:', error);
        }
      }
      else {
        differencesExist = true;
      }

      let response;
      if (!currentPaymentAdvanceId) {
        // If creating a new payment advance
        response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/create`, paymentAdvanceData);
        currentPaymentAdvanceId = response.data._id; // Update local variable with new ID
      } else {
        // If updating an existing payment advance
        response = await axios.put(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/${currentPaymentAdvanceId}`, paymentAdvanceData);
      }
      setModalData(response.data);

      //Add id to paymentAdvanceData
      paymentAdvanceData._id = currentPaymentAdvanceId; // Add _id to paymentAdvanceData

      let pdfFileMetadata = null;

      if (differencesExist) {
        try {
          // Call generateAndUploadPDF to create and upload the PDF, passing in your data and upload function
          console.log(paymentAdvanceData);
          pdfFileMetadata = await generateAndUploadPDF(paymentAdvanceData, getPresignedUrlAndUpload, differencesList);
          console.log('PDF uploaded successfully:', pdfFileMetadata);

        } catch (error) {
          console.error('Error generating or uploading PDF:', error);
          alert('Failed to generate or upload PDF.');
          return;
        }
      }

      // Prepare file metadata array, including the PDF file metadata if it exists
      const filesToSaveMetadata = pdfFileMetadata ? [{
        filename: pdfFileMetadata.filename,
        s3Key: pdfFileMetadata.s3Key,
        s3Url: pdfFileMetadata.s3Url,
        category: 'SOLICITUD DE ANTICIPO',
        entityId: currentPaymentAdvanceId,
        entityType: 'PaymentAdvance',
      }] : []; // Initialize as an empty array if no PDF was generated


      if (selectedFiles.length > 0) {
        const uploadedFilesMetadata = await Promise.all(selectedFiles.map(async (fileWithClassification) => {
          const metadata = await getPresignedUrlAndUpload(fileWithClassification.file);
          return {
            filename: metadata.filename,
            s3Key: metadata.s3Key,
            s3Url: metadata.s3Url,
            category: fileWithClassification.classification,
            entityId: currentPaymentAdvanceId,
            entityType: 'PaymentAdvance',
          };
        }));
        filesToSaveMetadata.push(...uploadedFilesMetadata);  // Concatenate the uploaded files metadata with the initial array
      }

      if (pdfFileMetadata || selectedFiles.length > 0) {
        // Save the metadata for all files, including the newly generated PDF
        try {
          await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/saveMetadata`, {
            files: filesToSaveMetadata,
            paymentAdvanceId: paymentAdvanceId, // Ensure this is defined, otherwise handle accordingly
          });
          console.log('File metadata saved successfully');
        } catch (error) {
          console.error('Error saving file metadata:', error);
          alert('Failed to save file metadata.');
          return; // Optionally stop the execution or handle error
        }
      }

      setUploadedFiles(filesToSaveMetadata); // Update the state with new metadata

      setIsModalVisible(true);

    } catch (error) {
      console.error("Error in the process:", error);
    }
  };

  useEffect(() => {
    if (paymentAdvanceId) {
      // Fetch and set existing payment advance data
      axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/${paymentAdvanceId}`)
        .then(response => {
          const data = response.data;
          setHeaderData({
            requestNumber: data._id,
            reference: passedRowData.traffic_reference,
            customer: passedRowData.customer_data?.customer_name,
            customer_key: passedRowData.customer_data?.customer_key,
            customer_rfc: passedRowData.customer_data?.customer_rfc,
            container_number: passedRowData.container_data?.container_number,
            container_type: passedRowData.container_data?.container_type,
            container_size: passedRowData.container_data?.container_size,
            pedimento: passedRowData.pedimento,
            license: passedRowData.license,
            entry_point: passedRowData.entry_point,
            tipo_pedimento: passedRowData.tipo_pedimento,
            status_pedimento: passedRowData.status_pedimento,
            status_modulacion: passedRowData.status_modulacion,
            number_of_reference_invoices: passedRowData.number_of_reference_invoices,
            status: data.status,
            container_data: passedRowData.container_data || [],
            bill_of_lading_data: passedRowData.bill_of_lading_data || [],
          });
          setExpenses({
            tax_expenses: data.tax_expenses,
            actual_expenses: data.actual_expenses,
            custom_brokerage_expenses: data.custom_brokerage_expenses
          });
          setAlternateCustomer(data.alternate_customer);
          // Check if the payment advance is under review and the user's role is not "Finance"
          if (data.status === "review" && currentUser?.profile?.email !== "jose.paz@wisdomfo.com") {
            alert("The Payment Advance is under review and cannot be changed.");
            navigate("/trafficDataGrid"); // Navigate to the trafficDataGrid
          }
        })
        .catch(error => console.error('Error fetching payment advance data:', error));
    }

  }, [paymentAdvanceId]);

  const sortFiles = (key) => {
    let direction = 'ascending';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    let sortedFiles = [...filesList];
    if (sortConfig !== null) {
      sortedFiles.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    setFilesList(sortedFiles);
  }, [sortConfig]);

  const validateData = () => {
    if (!headerData.reference || !headerData.customer || !headerData.pedimento) {
      alert("Mandatory fields are missing.");
      return false;
    }

    // Check for at least one expense in any category with a non-zero amount
    const hasValidExpense = ["tax_expenses", "actual_expenses", "custom_brokerage_expenses"].some(category => {
      return expenses[category].some(expense => expense.amount && parseFloat(expense.amount) > 0);
    });

    if (!hasValidExpense) {
      alert("At least one expense with a non-zero amount is required.");
      return false;
    }

    return true; // Validation passed
  };

  const [headerData, setHeaderData] = useState({
    reference: passedRowData.traffic_reference,
    customer: passedRowData.customer_data?.customer_name,
    customer_key: passedRowData.customer_data?.customer_key,
    customer_rfc: passedRowData.customer_data?.customer_rfc,
    container_number: passedRowData.container_data?.container_number,
    container_type: passedRowData.container_data?.container_type,
    container_size: passedRowData.container_data?.container_size,
    pedimento: passedRowData.pedimento,
    license: passedRowData.license,
    entry_point: passedRowData.entry_point,
    tipo_pedimento: passedRowData.tipo_pedimento,
    status_pedimento: passedRowData.status_pedimento,
    status_modulacion: passedRowData.status_modulacion,
    number_of_reference_invoices: passedRowData.number_of_reference_invoices,
    container_data: passedRowData.container_data || [],
    bill_of_lading_data: passedRowData.bill_of_lading_data || [],
  });

  const [expenses, setExpenses] = useState(initialChargesData);

  const handleDeleteFile = async (file, index) => {
    const isConfirmed = window.confirm(`Are you sure you want to delete ${file.filename}?`);
    if (!isConfirmed) {
      return;
    }

    try {
      // Assuming your backend expects the file's s3Key to delete the file
      await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/delete`, { data: { s3Key: file.s3Key } });

      // Remove the file from the local state to reflect the change immediately
      const updatedFilesList = [...filesList];
      updatedFilesList.splice(index, 1);
      setFilesList(updatedFilesList);

      // Determine the new status based on the remaining files
      let newStatus = 'open'; // Default status

      const hasComprobanteDeAnticipo = updatedFilesList.some(file => file.category === 'COMPROBANTE DE ANTICIPO');
      const hasFacturaPDF = updatedFilesList.some(file => file.category === 'FACTURA PDF');
      const hasFacturaXML = updatedFilesList.some(file => file.category === 'FACTURA XML');

      if (hasFacturaPDF && hasFacturaXML && hasComprobanteDeAnticipo) {
        newStatus = 'closed';
      } else if (hasComprobanteDeAnticipo) {
        newStatus = 'advance_received';
      }

      // Make a call to the backend to update the status
      await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_advance/${paymentAdvanceId}/update_status`, { status: newStatus });

      alert('File deleted successfully and status updated.');

    } catch (error) {
      console.error('Error deleting file or updating status:', error);
      alert('Failed to delete the file or update status.');
    }
  };

  return (
    <>
      <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
        <div className="flex items-center">
          <img src={logo} alt="Wisdom Logo" className="h-12 mr-2" />
          <span className="text-xl font-semibold text-gray-700"></span>
        </div>
        <div className="text-xl font-semibold text-gray-700">
          Payment Advance
        </div>
      </nav>

      <div className="min-h-screen bg-gray-100 p-6">
        {/* Header Component */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-3 p-2">
          <Header data={headerData} requestNumber={headerData.requestNumber} />
        </div>

        <div className="flex">
          {/* Column 1: Existing form and components */}
          <div className="flex-1">
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-6">
              <ChargesGrid
                expenses={expenses}
                setExpenses={setExpenses}
                alternateCustomer={alternateCustomer}
                setAlternateCustomer={setAlternateCustomer}
              />
            </div>
            <div className="mb-4">
              {!isViewOnly && (
                <button
                  className="bg-blue-900 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded"
                  onClick={handleSavePaymentAdvance}
                >
                  Save Payment Advance
                </button>
              )}
            </div>
          </div>

          {/* Column 2: New file list and upload components */}
          <div className="flex-1 ml-4">
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-6">
              <h2 className="text-xl font-semibold mb-4">Files</h2>
              {/* Separate container for headers */}
              <div className="grid grid-cols-5 text-gray-600 cursor-pointer sticky top-0 bg-white p-2 z-10">
                <p className="text-center" onClick={() => handleSort('filename')}>Name <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center" onClick={() => handleSort('size')}>Size <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center" onClick={() => handleSort('uploadDate')}>Uploaded <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center" onClick={() => handleSort('category')}>Type <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center">Action</p>
              </div>
              {/* Scrollable container for file list */}
              <div className="overflow-y-auto h-64">
                {/* File List Content */}
                {filesList.map((file, index) => (
                  <div key={index} className="grid grid-cols-6 items-center bg-gray-100 p-2 rounded-md mb-2 last:mb-0">
                    <p className="truncate col-span-2" title={file.filename}>{file.filename}</p>
                    <p className="text-center col-span-1">{isNaN(file.size) ? '—' : `${(file.size / 1024).toFixed(2)} KB`}</p>
                    <p className="text-center col-span-1">{new Date(file.uploadDate).toLocaleDateString()}</p>
                    <div className="col-span-1">
                      <span className="px-3 py-1 text-green-800 text-xs font-medium bg-green-100 rounded-full text-center block">
                        {file.category}
                      </span>
                    </div>
                    <div className="flex justify-center col-span-1 space-x-2">
                      <button onClick={() => handleViewFile(file._id)} className="text-blue-600 hover:text-blue-800">
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="lg" />
                      </button>
                      {!isViewOnly && (
                        <button onClick={() => handleDeleteFile(file, index)} className="text-red-600 hover:text-red-800">
                          <FontAwesomeIcon icon={faTrashAlt} size="lg" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

              </div>
            </div>

            {/* File Upload Component */}
            {!isViewOnly && (

              <div className="bg-white shadow rounded-lg overflow-hidden p-6">
                <FileUploadComponent onFilesSelected={onFilesSelected} usageContext="PaymentAdvance" />
              </div>
            )}
          </div>
        </div>

        {/* Modal JSX */}
        {isModalVisible && (
          <div className="fixed z-10 inset-0 bg-black bg-opacity-60 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="bg-white p-6 rounded-xl shadow-2xl border-t-4 border-blue-900 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                <h2 className="text-2xl font-bold mb-4 text-indigo-600">Payment Advance Created Successfully!</h2>
                <p className="mb-4 text-gray-700"><strong>Request Number:</strong> {modalData._id}</p>
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full divide-y divide-gray-300">
                    <tbody className="bg-white divide-y divide-gray-300">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Created Date</td>
                        <td className="px-6 py-4 whitespace-nowrap">{new Date(modalData.created_date).toLocaleDateString()}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Total Requested Payment Advance</td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatAmount(modalData.total_requested_payment_advance)}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Actual Payment Advance</td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatAmount(modalData.actual_payment_advance)}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Tax Expenses</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ul>
                            {modalData.tax_expenses.map((tax, index) => (
                              <li key={index}>
                                {tax.concept}: {formatAmount(tax.amount)} (Added: {new Date(tax.date_added).toLocaleDateString()})
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Total Taxes</td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatAmount(modalData.total_taxes)}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Actual Expenses</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ul>
                            {modalData.actual_expenses.map((expense, index) => (
                              <li key={index}>
                                {expense.concept}: {formatAmount(expense.amount)} (Added: {new Date(expense.date_added).toLocaleDateString()})
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Total Actual Expenses</td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatAmount(modalData.total_actual_expenses)}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Custom Brokerage Expenses</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ul>
                            {modalData.custom_brokerage_expenses.map((brokerage, index) => (
                              <li key={index}>
                                {brokerage.concept}: {formatAmount(brokerage.amount)} (Added: {new Date(brokerage.date_added).toLocaleDateString()})
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">Total Custom Brokerage</td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatAmount(modalData.total_custom_brokerage)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => {
                    setIsModalVisible(false);

                    triggerRefetch();

                    navigate("/trafficDataGrid");
                  }}
                  className="mt-4 px-4 py-2 border border-blue-900 text-sm font-medium rounded-md text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
        }
      </div>
    </>
  );

};

export default PaymentAdvance;
